"use client";

import React from "react";
import { Factory, Building2, Store, ShieldCheck, Clock, HardHat, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Commercial Exteriors",
  description: "Professional exterior painting for shops, offices, and commercial buildings, delivering durable finishes that improve street appeal and protect your property from the coastal climate.",
  icon: Factory
}, {
  title: "Office Interiors",
  description: "Professional, low-VOC painting solutions that minimize disruption to your operations and create an inspiring, clean workspace for your team.",
  icon: Building2
}, {
  title: "Retail Spaces",
  description: "Flawless, brand-aligned finishes completed with efficient turnaround times, ensuring your doors stay open and your business looks its best.",
  icon: Store
}];
const features = [{
  text: "Strict WHS Safety Compliance",
  icon: ShieldCheck
}, {
  text: "Efficient Turnaround Times",
  icon: Clock
}, {
  text: "Fully Insured",
  icon: HardHat
}];
export default function IndustrialCommercialPainting() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const cardsReveal = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section id="industrial-commercial-painting" className="bg-secondary text-secondary-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
      backgroundSize: '32px 32px'
    }}></div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">Commercial <span className="text-accent" data-ast-id="f:/src/pages/Services/sections/IndustrialCommercialPainting.tsx|sig:lnwc6t" data-ast-start="2227" data-ast-end="2272">Painting</span></h2>
          <p className="text-lg text-secondary-foreground/80 mb-8">
            Delivering "The Perfect Finish" for large-scale projects across the Northern Central Coast. We understand that commercial painting requires minimal disruption, strict safety adherence, and uncompromising quality.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            return <div key={i} data-index={i} className="flex items-center gap-2 text-sm md:text-base font-medium text-secondary-foreground/90 bg-secondary-foreground/10 px-4 py-2 rounded-full">
                  <Icon className="w-5 h-5 text-accent" />
                  <span>{feature.text}</span>
                </div>;
          })}
          </div>
        </div>

        <div ref={cardsReveal.ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 transition-all duration-1000 delay-200 ${cardsReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {services.map((service, i) => {
          const Icon = service.icon;
          return <Card key={i} data-index={i} className="bg-primary border-none shadow-xl hover:-translate-y-2 transition-transform duration-300">
                <CardHeader>
                  <div className="w-14 h-14 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl text-primary-foreground">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-primary-foreground/70 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="text-center">
          <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-8 py-6 text-lg rounded-full shadow-lg shadow-accent/20">
            <Link to="/ContactPage">
              Get a Commercial Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-secondary-foreground/60">
            Or call Clint directly at <Link to="tel:0413771815" className="text-accent hover:underline">0413 771 815</Link>
          </p>
        </div>
      </div>
    </section>;
}